import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DoubleYAxisLineChart from './DoubleYAxisLineChart/DoubleYAxisLineChart';
import { useDebouncedResizeObserver } from '~/shared/hooks';
import { downloadHeatmapData, fetchTopicExplainability } from '~/shared/services/api';
import styled from 'styled-components';
import { Paper, Fab } from '@mui/material';
import FilterBar from './FilterBar';
import { AlertBar, Loader, SeriesControls } from '~/shared/components';
import { useNavAndFilters, useHeatmapData, useScroller } from '~/shared/services/charts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import moment from 'moment';
import IndicatorTable from './Indicator/IndicatorTable';
import IndicatorStackedAreaChart from './Indicator/IndicatorStackedAreaChart';
import { tooltipCfg } from '~/shared/config/tooltipCfg';
import { colors, plotBandOpacity } from '~/shared/constants';
import { hex2rgba } from '../../utils/colors/hex2rgba';
import { DEFAULT_RANGE_SELECTOR_INDEX, getRangeSelectorIndexFromWindow } from '~/shared/utils/timeWindow';
import { buildDoubleYAxisHeatmapSeriesData } from '~/shared/utils/doubleYAxisLegend';
import type {
  TopicExplainabilityExternalState,
  TopicExplainabilityPhase,
  TopicExplainabilityStateChangeHandler,
} from './topicExplainability.types';
import {
  normalizeExplainabilityTitle,
  resolveExplainabilityDestinationTitle,
  transformExplainabilityResponse,
} from './topicExplainability.utils';

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  ${({ theme }: any) => `
    padding: ${theme.spacing(3)}px;
  `};
`;

const IndicatorsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const IndicatorContainer = styled.div<{ $iswide: number }>`
  min-width: calc(50% - 5px);
  ${({ $iswide }) => $iswide === 1 && 'flex: 1;'}
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 32px;
  right: 80px;
  gap: 6px;
  align-items: flex-end;
`;

const MainContainer = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ContentContainer = styled.div`
  display: flex;
  padding: 4px;
`;

const HeatmapContainer = styled.div`
  flex: 1;
  position: relative;
  padding: 8px;
`;

const FabContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--color-blue-2);
    color: var(--color-white);
  }
`;

const RelativeContainer = styled.div`
  position: relative;
  height: 100%;
`;

const AlignedLoader = styled(Loader)`
  position: fixed;
  left: calc(50% - 25px);
  top: calc(50% - 25px);
`;

function useHeatmapClick(navHirarchyItem: any, onScroll: any, filterValues: any) {
  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  useEffect(() => {
    setIndicatorSections([]);
  }, [navHirarchyItem, filterValues]);
  const onClick = useCallback(
    (cellInfo: { source: string; topic?: string; symbol?: string }) => {
      setIndicatorSections([]);
      setTimeout(
        () =>
          navHirarchyItem &&
          filterValues &&
          setIndicatorSections(
            navHirarchyItem?.indicators[cellInfo?.source]?.map((indicatorObj: any) => {
              const indicator = indicatorObj && indicatorObj[0];
              const type = indicatorObj && indicatorObj[1];
              return { indicator, cellInfo, type };
            }),
          ),
        10,
      );
      setTimeout(onScroll, 200);
    },
    [navHirarchyItem, filterValues, onScroll],
  );
  return { indicatorSections, onClick };
}

export default function DoubleYAxisLineChartPage({
  navHirarchy,
  gridIndex,
  gridHeight,
  onGridClick,
  gridFilterValues,
  gridFilterValuesID,
  gridPrism,
  onTopicExplainabilityStateChange,
}: any) {
  const [chartInstanceKey, setChartInstanceKey] = useState(0);
  const previousTitleRef = useRef<string | null>(null);
  const { scrollerRef, scrollToTop, scrollToBottom } = useScroller();
  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);

  const normalizedGridIndex = typeof gridIndex === 'string' ? Number(gridIndex) : gridIndex;
  const isPrimaryNarrativeChart =
    normalizedGridIndex === undefined || normalizedGridIndex === null || normalizedGridIndex === 0;

  const {
    data,
    error,
    isLoading,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    chartYtitle2,
    defaultTimeWindow,
    yAxisRangeMode,
    yAxisPaddingRatio,
    dropdownLegendEnabled,
  } = useHeatmapData(category, subcategory, subsubcategory, prism, gridFilterValuesID || filterValuesID, prismId);

  const allowDropdownLegend = dropdownLegendEnabled;

  const resolvedChartTitle = useMemo(() => {
    if (chartTitle && chartTitle.trim().length > 0) {
      return chartTitle.trim();
    }
    const fallback = navHirarchyItem?.heatmap?.title ?? navHirarchyItem?.heatmap?.name ?? navHirarchyItem?.name ?? '';
    return (fallback || '').trim();
  }, [chartTitle, navHirarchyItem]);

  useEffect(() => {
    const normalizedTitle = resolvedChartTitle || '';
    if (previousTitleRef.current === null) {
      previousTitleRef.current = normalizedTitle;
      return;
    }
    if (previousTitleRef.current !== normalizedTitle) {
      previousTitleRef.current = normalizedTitle;
      setChartInstanceKey(prev => prev + 1);
    }
  }, [resolvedChartTitle]);

  const defaultRangeIndex = useMemo(() => getRangeSelectorIndexFromWindow(defaultTimeWindow), [defaultTimeWindow]);

  const [selectedRangeSelector, setSelectedRangeSelector] = useState<number>(
    () => defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX,
  );
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [selectedSeriesNames, setSelectedSeriesNames] = useState<string[] | null>(null);
  const [seriesOptions, setSeriesOptions] = useState<Array<{ name: string; color?: string; visible: boolean }>>([]);

  useEffect(() => {
    const nextIndex = defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX;
    setSelectedRangeSelector(prev => (prev === nextIndex ? prev : nextIndex));
  }, [defaultRangeIndex, data]);

  useEffect(() => {
    setSelectedSeriesNames(null);
  }, [data]);

  const apiData = useMemo(() => {
    if (
      !data ||
      !Array.isArray(data.col_nodes) ||
      !data.col_nodes.length ||
      !Array.isArray(data.row_nodes) ||
      !data.row_nodes.length ||
      !Array.isArray(data.data)
    ) {
      return undefined;
    }

    const obj = data;
    const colNodes = obj.col_nodes;
    const colorScheme =
      obj?.color_scheme ||
      colors
        .slice(-colNodes.length)
        .reverse()
        .map((color: string) => getComputedStyle(document.documentElement).getPropertyValue(color));

    let plotBands;

    if (obj?.highcharts_config?.yAxis_custom_background_colors) {
      const valuesBg = obj?.highcharts_config?.yAxis_custom_background_values;
      const colorsBg = obj?.highcharts_config?.yAxis_custom_background_colors;
      const labelsBg = obj?.highcharts_config?.yAxis_custom_background_labels;
      plotBands = colorsBg?.map((color: string, i: number) => ({
        from: valuesBg[i],
        to: valuesBg[i + 1],
        color: hex2rgba(color, plotBandOpacity),
        label: {
          text: labelsBg[i],
          useHtml: true,
          style: {
            color: '#000000',
          },
        },
      }));
    }

    const yLabels1 = colNodes.map((c: any) => c.name);
    const { seriesData1, seriesData2 } = buildDoubleYAxisHeatmapSeriesData(obj, {
      parseX: rawX => {
        if (typeof rawX === 'number' && Number.isFinite(rawX)) {
          return rawX;
        }
        return moment.utc(rawX).valueOf();
      },
      resolveColor: (_seriesIndex, columnIndex, isSecondary) => {
        const paletteIndex = isSecondary ? columnIndex : yLabels1.length - columnIndex - 1;
        return colorScheme?.[paletteIndex] || undefined;
      },
    });
    const numberOfLines = obj?.number_of_lines;
    return { seriesData1, seriesData2, colorScheme, plotBands, numberOfLines };
  }, [data]);

  const topicExplainabilityRequestId = useRef(0);

  const narrativeTopic = useMemo(() => {
    const source = gridFilterValues || filterValues || {};
    const rawTopic =
      source?.Narrative ??
      source?.narrative ??
      source?.['Narrative'] ??
      source?.['narrative'] ??
      filterValuesID?.Narrative ??
      filterValuesID?.narrative ??
      '';

    if (typeof rawTopic === 'string') return rawTopic;
    if (rawTopic && typeof rawTopic === 'object') {
      if ('name' in rawTopic && rawTopic.name) return String(rawTopic.name);
      if ('value' in rawTopic && rawTopic.value) return String(rawTopic.value);
      if ('id' in rawTopic && rawTopic.id) return String(rawTopic.id);
    }
    return rawTopic ? String(rawTopic) : '';
  }, [gridFilterValues, filterValues, filterValuesID]);

  const normalizedNarrativeTopic = useMemo(() => narrativeTopic.trim(), [narrativeTopic]);

  const chartExplainabilitySourceTitle = useMemo(() => resolvedChartTitle, [resolvedChartTitle]);

  const explicitDestinationTitle = useMemo(() => {
    const heatmap = navHirarchyItem?.heatmap ?? {};
    return heatmap?.childTable ?? heatmap?.child_table ?? heatmap?.['child-table'] ?? heatmap?.childtable ?? null;
  }, [navHirarchyItem]);

  const resolvedExplainabilityDestinationTitle = useMemo(
    () => resolveExplainabilityDestinationTitle(chartExplainabilitySourceTitle, explicitDestinationTitle),
    [chartExplainabilitySourceTitle, explicitDestinationTitle],
  );

  const hasExplainabilityDestination = useMemo(
    () => Boolean(normalizeExplainabilityTitle(resolvedExplainabilityDestinationTitle)),
    [resolvedExplainabilityDestinationTitle],
  );

  const emitTopicExplainabilityChange = useCallback(
    (phase: TopicExplainabilityPhase, overrides?: Partial<TopicExplainabilityExternalState>) => {
      const handler = onTopicExplainabilityStateChange as TopicExplainabilityStateChangeHandler | undefined;
      if (!handler) return;
      handler({
        topic: overrides?.topic ?? normalizedNarrativeTopic,
        phase,
        loading: overrides?.loading ?? phase === 'loading',
        error: overrides?.error ?? null,
        data: overrides?.data ?? null,
        destinationTitle: overrides?.destinationTitle ?? null,
        sourceTitle: overrides?.sourceTitle ?? null,
        requestId: overrides?.requestId ?? 0,
      });
    },
    [normalizedNarrativeTopic, onTopicExplainabilityStateChange],
  );

  const handleTopicExplainabilityRequest = useCallback(
    async (topic?: string) => {
      if (!isPrimaryNarrativeChart) {
        return;
      }
      const fallbackTopic = topic ?? normalizedNarrativeTopic;
      const normalizedTopic = (fallbackTopic || '').trim();
      const sourceTitle = chartExplainabilitySourceTitle;
      if (!normalizedTopic) {
        emitTopicExplainabilityChange('idle', {
          topic: '',
          loading: false,
          data: null,
          error: null,
          destinationTitle: null,
          sourceTitle,
          requestId: topicExplainabilityRequestId.current,
        });
        return;
      }

      topicExplainabilityRequestId.current += 1;
      const requestId = topicExplainabilityRequestId.current;

      if (hasExplainabilityDestination && resolvedExplainabilityDestinationTitle) {
        emitTopicExplainabilityChange('loading', {
          topic: normalizedTopic,
          loading: true,
          data: null,
          error: null,
          destinationTitle: resolvedExplainabilityDestinationTitle,
          sourceTitle,
          requestId,
        });
        return;
      }

      emitTopicExplainabilityChange('loading', {
        topic: normalizedTopic,
        loading: true,
        data: null,
        error: null,
        destinationTitle: null,
        sourceTitle,
        requestId,
      });

      try {
        const response = await fetchTopicExplainability(normalizedTopic);
        const parsed = transformExplainabilityResponse(response);
        if (topicExplainabilityRequestId.current === requestId) {
          emitTopicExplainabilityChange('success', {
            topic: normalizedTopic,
            loading: false,
            data: parsed,
            error: null,
            destinationTitle: null,
            sourceTitle,
            requestId,
          });
        }
      } catch (err) {
        if (topicExplainabilityRequestId.current === requestId) {
          const message = err instanceof Error ? err.message : 'Failed to load topic explainability ';
          emitTopicExplainabilityChange('error', {
            topic: normalizedTopic,
            loading: false,
            data: null,
            error: message,
            destinationTitle: null,
            sourceTitle,
            requestId,
          });
        }
      }
    },
    [
      emitTopicExplainabilityChange,
      isPrimaryNarrativeChart,
      normalizedNarrativeTopic,
      hasExplainabilityDestination,
      resolvedExplainabilityDestinationTitle,
      chartExplainabilitySourceTitle,
    ],
  );

  const { size, ref: chartSizeRef }: any = useDebouncedResizeObserver(100);
  const containerWidth = size?.width;
  const isNarrowChart = containerWidth && containerWidth < 600;

  const seriesOptionNames = useMemo(() => seriesOptions.map(option => option.name), [seriesOptions]);
  const defaultPartialSelection = useMemo(
    () => seriesOptions.filter(option => option.visible).map(option => option.name),
    [seriesOptions],
  );
  const showSeriesSelector = allowDropdownLegend && seriesOptions.length > 1;
  const isSeriesSelectDisabled = !allowDropdownLegend || !seriesOptions.length;
  const shouldRenderControls = Boolean(data && !isLoading && !isExportMenuOpen && showSeriesSelector);

  const handleSeriesMetadataChange = useCallback((meta: Array<{ name: string; color?: string; visible: boolean }>) => {
    setSeriesOptions(meta);
  }, []);

  const handleSeriesSelectionChange = useCallback(
    (selection: string[]) => {
      const selectionSet = new Set(selection);
      const orderedSelection = seriesOptionNames.filter(name => selectionSet.has(name));
      if (orderedSelection.length === seriesOptionNames.length) {
        setSelectedSeriesNames(null);
        return;
      }
      setSelectedSeriesNames(orderedSelection);
    },
    [seriesOptionNames],
  );

  useEffect(() => {
    if (!data) {
      setIsExportMenuOpen(false);
    }
  }, [data]);

  const { indicatorSections, onClick } = useHeatmapClick(
    navHirarchyItem,
    scrollToBottom,
    gridFilterValues || filterValues,
  );

  useEffect(() => {
    if (!isPrimaryNarrativeChart) return;
    const resetRequestId = ++topicExplainabilityRequestId.current;
    emitTopicExplainabilityChange('idle', {
      topic: '',
      loading: false,
      data: null,
      error: null,
      destinationTitle: null,
      sourceTitle: chartExplainabilitySourceTitle,
      requestId: resetRequestId,
    });
  }, [
    emitTopicExplainabilityChange,
    filterValuesID,
    gridFilterValuesID,
    isPrimaryNarrativeChart,
    normalizedNarrativeTopic,
    chartExplainabilitySourceTitle,
  ]);

  const handleOnGridClick = useCallback(
    (props: any) => {
      onGridClick({
        ...props,
        navHirarchyItem,
        filterValues: gridFilterValues || filterValues,
        category,
        subcategory,
        subsubcategory,
        prism,
        prismId,
        indicatorSections,
      });
    },
    [
      navHirarchyItem,
      filterValues,
      gridFilterValues,
      category,
      subcategory,
      subsubcategory,
      prism,
      prismId,
      indicatorSections,
      onGridClick,
    ],
  );

  const onDownload = () =>
    downloadHeatmapData(
      category,
      subcategory,
      subsubcategory,
      prism,
      gridFilterValuesID || filterValuesID,
      prismId,
      resolvedChartTitle,
    ).catch(e => {
      alert(e.message);
    });

  return (
    <ScrollWrap ref={scrollerRef}>
      {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      <MainContainer>
        <ContentContainer>
          <HeatmapContainer ref={chartSizeRef}>
            <RelativeContainer>
              {isLoading ? (
                <AlignedLoader />
              ) : (
                apiData && (
                  <DoubleYAxisLineChart
                    key={chartInstanceKey}
                    {...{
                      data: apiData,
                      size,
                      onClick: gridIndex || gridIndex === 0 ? handleOnGridClick : onClick,
                      prism,
                      filterValues: gridFilterValues || filterValues,
                      colorScheme: apiData?.colorScheme,
                      chartTitle: resolvedChartTitle,
                      chartSubtitle,
                      chartYtitle,
                      chartYtitle2,
                      tooltipCfg,
                      gridHeight,
                      gridIndex,
                      gridPrism,
                      plotBands: apiData?.plotBands,
                      numberOfLines: apiData?.numberOfLines,
                      selectedRangeSelector,
                      setSelectedRangeSelector,
                      selectedSeriesNames,
                      onSelectedSeriesNamesChange: setSelectedSeriesNames,
                      onSeriesMetadataChange: handleSeriesMetadataChange,
                      dropdownLegendEnabled: allowDropdownLegend,
                      onExportMenuVisibilityChange: setIsExportMenuOpen,
                      onTopicExplainabilityRequest: handleTopicExplainabilityRequest,
                      topicForExplainability: normalizedNarrativeTopic,
                      yAxisRangeMode,
                      yAxisPaddingRatio,
                    }}
                  />
                )
              )}
            </RelativeContainer>
          </HeatmapContainer>
          {!gridIndex && gridIndex !== 0 && (
            <FilterBar
              type={navHirarchyItem?.type}
              filters={navHirarchyItem?.filters}
              values={gridFilterValues || filterValues}
              filterHierarchy={navHirarchyItem?.narrativeFilterTree}
              filterHierarchyValues={gridFilterValuesID || filterValuesID}
              onDownload={onDownload}
              tooltipCfg={tooltipCfg}
            />
          )}
        </ContentContainer>
        {shouldRenderControls && (
          <ControlsWrapper>
            <SeriesControls
              compact={isNarrowChart}
              invertMenuOrder={false}
              seriesOptions={showSeriesSelector ? seriesOptions : []}
              selectedSeries={selectedSeriesNames}
              defaultPartialSelection={defaultPartialSelection}
              onSeriesChange={handleSeriesSelectionChange}
              seriesDisabled={isSeriesSelectDisabled || isLoading}
              seriesLabel="Series:"
              menuWidth={isNarrowChart ? 180 : 220}
              seriesTooltipCfg={tooltipCfg}
            />
          </ControlsWrapper>
        )}
      </MainContainer>
      <IndicatorsContainer>
        {!gridIndex &&
          gridIndex !== 0 &&
          indicatorSections.map((section: any, index: number) => (
            <IndicatorContainer
              key={'container' + section.indicator + section.topic + section.symbol + String(index)}
              $iswide={indicatorSections.length === index + 1 && indicatorSections.length % 2 !== 0 ? 1 : 0}
            >
              {section.type === 'bar' && (
                <IndicatorStackedAreaChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorStackedAreaChart>
              )}
              {section.type === 'line' && (
                <IndicatorLineChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorLineChart>
              )}
              {section.type === 'table' && (
                <IndicatorTable
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorTable>
              )}
            </IndicatorContainer>
          ))}
      </IndicatorsContainer>
      <FabContainer>
        {indicatorSections.length > 0 && (
          <Fab onClick={scrollToTop}>
            <ExpandLess />
          </Fab>
        )}
      </FabContainer>
    </ScrollWrap>
  );
}
